'use client';

import type { FormErrors, UseFormReturnType } from '@mantine/form';
import type { FormEvent, ReactNode } from 'react';
import React, { createContext, useContext, useId } from 'react';

export type FormContextType<Values = Record<string, unknown>, TransformedValues = Values> = {
	formId: string;
	form: UseFormReturnType<Values, TransformedValues>;
	disabled?: boolean;
	hasSubmitted: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = createContext<FormContextType<any, any> | null>(null);

export type FormProps<Values = Record<string, unknown>, TransformedValues = Values> = {
	className?: string;
	children?: ReactNode;
	form: UseFormReturnType<Values, TransformedValues>;
	id?: string;
	disabled?: boolean;
	onSubmit?: (values: TransformedValues, event: FormEvent<HTMLFormElement> | undefined) => void;
	onValidationFailure?: (errors: FormErrors, values: Values, event: FormEvent<HTMLFormElement> | undefined) => void;
};

export default function Form<Values = Record<string, unknown>, TransformedValues = Values>(
	props: FormProps<Values, TransformedValues>,
) {
	const { className, children, form, disabled, onSubmit, onValidationFailure } = props;

	const [hasSubmitted, setHasSubmitted] = React.useState(false);

	// `useId` gives an SSR-stable id: `uuid()` ran independently on server and client and
	// produced different values, which hydrates as a mismatch on every form (React 19 no
	// longer silently patches attribute mismatches). An explicit `props.id` still wins.
	const generatedId = useId();
	const formId = props.id ?? generatedId;

	const formContext: FormContextType<Values, TransformedValues> = {
		form,
		formId,
		disabled,
		hasSubmitted,
	};

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setHasSubmitted(true);

		// We need to wrap the handleSubmit and handleValidationError callbacks otherwise validation doesn't work properly
		// due to potentially unset callbacks
		form.onSubmit(
			(values: TransformedValues, event: FormEvent<HTMLFormElement> | undefined) => {
				onSubmit?.(values, event);
			},
			(errors: FormErrors, values: Values, event: FormEvent<HTMLFormElement> | undefined) => {
				onValidationFailure?.(errors, values, event);
			},
		)(e);
	};

	return (
		<FormContext.Provider value={formContext}>
			<form className={`Form ${className ?? ''}`} id={formContext.formId} onSubmit={handleSubmit}>
				{children}
			</form>
		</FormContext.Provider>
	);
}

export function useFormContext<V, T = V>() {
	return useContext(FormContext) as FormContextType<V, T>;
}
